import type { ReactNode } from 'react'

import type { Api, EventLoggerData, Page, Router } from '@subaio/utils'
import type { Container as ContainerBase, Params } from '@subaio/webapp/src/containers'

import { LoadingScreen } from 'components/Layout'

import type { PageviewOptions } from './abnTracking'
import type { Store } from './store'

export type OnLogEvent = (identifier: string, data?: EventLoggerData) => void

export type Extensions<TParams extends Params, TState extends Record<string, unknown>> = {
	path?: string
	title?: ReactNode | ((props: TParams & TState) => ReactNode | undefined)
}

// biome-ignore lint/correctness/noUnusedVariables: Keep type variable in case we need dependencies later
export type Props<TState extends Record<string, unknown>, TDependencies extends keyof TState> = {
	query: Page['query']
	clientApi: Api
	setData: (update: Partial<TState>) => void
	onLogEvent: OnLogEvent
	onLogPageView: (form: PageviewOptions['form']) => void
	// Include store to easier use stack-based steps from widgetapps
	store: Store
}

export type InitProps = {
	query: Page['query']
	router: Router
	clientApi: Api
	updatePage: (page: Page) => void
}

export type Container<
	TState extends Record<string, unknown> = Record<string, any>,
	TParams extends Params = Record<string, any>,
	TDefaults extends keyof TState = keyof TState,
	TDependencies extends keyof TState = keyof TState,
> = ContainerBase<
	Extensions<TParams, TState>,
	Props<TState, TDependencies>,
	InitProps,
	TState,
	TParams,
	TDefaults,
	TDependencies
>

export const asContainer =
	<TState extends Record<string, unknown> = {}, TParams extends Params = {}>() =>
	<TDefaults extends keyof TState = never, TDependencies extends keyof TState = never>(
		container: Omit<
			ContainerBase<
				Extensions<TParams, TState>,
				Props<TState, TDependencies>,
				InitProps,
				TState,
				TParams,
				TDefaults,
				TDependencies
			>,
			'Loading'
		>,
	) =>
		({
			...container,
			Loading: () => <LoadingScreen />,
		}) as ContainerBase<
			Extensions<TParams, TState>,
			Props<TState, TDependencies>,
			InitProps,
			TState,
			TParams,
			TDefaults,
			TDependencies
		>
